import React from 'react';
import './PrivacyPolicyPage.scss';

interface Section {
  number: string;
  title: string;
  content: React.ReactNode;
}

const sections: Section[] = [
  {
    number: '1',
    title: 'Introduction',
    content: (
      <>
        <p>Welcome to CricCenter Inc. ("CricCenter," "we," "us," or "our"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Platform").</p>
        <p>By accessing or using our Platform, you agree to the terms of this Privacy Policy. If you do not agree, please do not use our Platform.</p>
      </>
    ),
  },
  {
    number: '2',
    title: 'Data Controller',
    content: (
      <p>CricCenter Inc. acts as the Data Controller for personal information collected through the Platform. We are responsible for deciding how and why your personal data is processed.</p>
    ),
  },
  {
    number: '3',
    title: 'Data Processor',
    content: (
      <p>In certain contexts, CricCenter may act as a Data Processor on behalf of leagues, clubs, or organizations that use our Platform. In such cases, the organization acts as the Data Controller, and we process data according to their instructions and applicable agreements.</p>
    ),
  },
  {
    number: '4',
    title: 'Scope of This Policy',
    content: (
      <p>This Privacy Policy applies to all users of the CricCenter Platform, including players, coaches, scorers, organizers, viewers, and any other individuals who interact with our services. It covers data collected through our mobile app, website, APIs, and any affiliated services.</p>
    ),
  },
  {
    number: '5',
    title: 'Information We Collect',
    content: (
      <>
        <p>We collect several types of information to provide and improve our services:</p>
        <div className="policy-subsection">
          <h4>5.1 Account Information</h4>
          <p>When you register for an account, we collect your name, email address, phone number, date of birth, username, and password. We may also collect profile information such as your profile picture, bio, and cricketing role (player, coach, scorer, etc.).</p>
        </div>
        <div className="policy-subsection">
          <h4>5.2 Cricket Performance Data</h4>
          <p>We collect data related to your cricket performance, including match statistics, scores, bowling/batting records, fielding data, team affiliations, and historical performance records.</p>
        </div>
        <div className="policy-subsection">
          <h4>5.3 Location Information</h4>
          <p>With your permission, we collect location data to help identify nearby matches, grounds, and teams. You can disable location permissions at any time through your device settings.</p>
        </div>
        <div className="policy-subsection">
          <h4>5.4 Device and Technical Information</h4>
          <p>We automatically collect certain technical information including device type, operating system, IP address, browser type, app version, unique device identifiers, and crash reports.</p>
        </div>
        <div className="policy-subsection">
          <h4>5.5 Usage Data</h4>
          <p>We collect information about how you use our Platform, including pages viewed, features used, time spent, interactions, and navigation patterns.</p>
        </div>
        <div className="policy-subsection">
          <h4>5.6 Communications</h4>
          <p>We collect information from communications you send to us, including support requests, feedback, and responses to surveys or promotions.</p>
        </div>
        <div className="policy-subsection">
          <h4>5.7 Payment Information</h4>
          <p>When you make purchases, payment processing is handled by secure third-party payment processors. We do not store your full credit card or payment details on our servers.</p>
        </div>
      </>
    ),
  },
  {
    number: '6',
    title: 'How We Collect Information',
    content: (
      <p>We collect information directly from you when you register, update your profile, or interact with our Platform. We also collect information automatically through cookies, analytics tools, and device data when you use our services. In some cases, we may receive information about you from third parties, such as social media platforms (if you connect your account), sports organizations, or our business partners.</p>
    ),
  },
  {
    number: '7',
    title: 'How We Use Your Information',
    content: (
      <>
        <p>We use the information we collect for the following purposes:</p>
        <div className="policy-subsection">
          <h4>7.1 Providing Services</h4>
          <p>To create and manage your account, facilitate match scoring and live scoring, display performance statistics, enable team and tournament management, and deliver all core features of the Platform.</p>
        </div>
        <div className="policy-subsection">
          <h4>7.2 Personalization</h4>
          <p>To personalize your experience, recommend relevant content, matches, players, and teams based on your preferences and activity.</p>
        </div>
        <div className="policy-subsection">
          <h4>7.3 Communications</h4>
          <p>To send you account-related notifications, updates about matches or leagues, promotional communications (where you have consented), and responses to your inquiries.</p>
        </div>
        <div className="policy-subsection">
          <h4>7.4 Analytics and Improvement</h4>
          <p>To analyze usage patterns, monitor Platform performance, identify bugs and issues, and continuously improve our services and user experience.</p>
        </div>
        <div className="policy-subsection">
          <h4>7.5 Safety and Security</h4>
          <p>To detect, prevent, and address fraud, abuse, and security threats. To verify your identity and enforce our Terms of Service.</p>
        </div>
        <div className="policy-subsection">
          <h4>7.6 Legal Compliance</h4>
          <p>To comply with applicable laws, regulations, legal processes, and enforce our legal rights.</p>
        </div>
      </>
    ),
  },
  {
    number: '8',
    title: 'Legal Bases for Processing',
    content: (
      <>
        <p>Depending on your jurisdiction, we rely on the following legal bases to process your personal data:</p>
        <ul>
          <li><strong>Contract Performance:</strong> Processing necessary to provide the services you've requested.</li>
          <li><strong>Legitimate Interests:</strong> Processing for our legitimate business interests, such as improving our Platform and preventing fraud.</li>
          <li><strong>Consent:</strong> Processing based on your explicit consent, which you can withdraw at any time.</li>
          <li><strong>Legal Obligation:</strong> Processing necessary to comply with our legal obligations.</li>
        </ul>
      </>
    ),
  },
  {
    number: '9',
    title: 'Public Cricket Records',
    content: (
      <p>Match scores, team standings, player statistics, and other cricket records entered into our Platform may be considered public information. By participating in matches on CricCenter, you acknowledge that your performance data may be visible to other users, including the public, depending on the privacy settings of the relevant league or tournament organizer.</p>
    ),
  },
  {
    number: '10',
    title: "Children's Privacy",
    content: (
      <p>Our Platform is not intended for children under the age of 13 (or the applicable age of digital consent in your jurisdiction). We do not knowingly collect personal information from children under 13. If we discover that we have inadvertently collected data from a child under 13, we will promptly delete it. If you believe a child has provided us with personal information, please contact us at privacy@criccenter.com.</p>
    ),
  },
  {
    number: '11',
    title: 'Sensitive Personal Information',
    content: (
      <p>We do not intentionally collect sensitive personal information such as health or medical data. If you choose to share any such information voluntarily (e.g., in a player profile or bio), you do so at your own discretion. We will treat any such data with the highest level of care and in accordance with applicable laws.</p>
    ),
  },
  {
    number: '12',
    title: 'Media and Content Consent',
    content: (
      <p>By uploading photos, videos, or other media content to our Platform, you grant CricCenter a non-exclusive, worldwide, royalty-free license to use, display, and distribute such content in connection with our services. You confirm that you have the necessary rights and consents for all media you upload, including consent from any individuals depicted.</p>
    ),
  },
  {
    number: '13',
    title: 'Subscriptions and Billing',
    content: (
      <p>If you subscribe to a paid plan, your subscription information, including plan type and billing history (but not full payment details), is stored in our systems. Payment transactions are processed by third-party payment processors who have their own privacy policies. We encourage you to review the privacy policies of our payment processors.</p>
    ),
  },
  {
    number: '14',
    title: 'Cookies and Tracking Technologies',
    content: (
      <>
        <p>We use cookies, web beacons, and similar tracking technologies to enhance your experience on our Platform. These technologies help us:</p>
        <ul>
          <li>Keep you logged in to your account</li>
          <li>Remember your preferences and settings</li>
          <li>Analyze Platform usage and performance</li>
          <li>Deliver relevant content and features</li>
        </ul>
        <p>You can control cookie settings through your browser or device settings. Please note that disabling certain cookies may affect the functionality of our Platform.</p>
      </>
    ),
  },
  {
    number: '15',
    title: 'Data Sharing and Disclosure',
    content: (
      <>
        <p>We may share your information in the following circumstances:</p>
        <div className="policy-subsection">
          <h4>15.1 Service Providers</h4>
          <p>We share data with trusted third-party service providers who assist us in operating our Platform, including cloud hosting providers, analytics services, customer support platforms, and payment processors. These providers are contractually bound to protect your data and use it only for the purposes we specify.</p>
        </div>
        <div className="policy-subsection">
          <h4>15.2 Leagues and Organizations</h4>
          <p>If you participate in a league or tournament managed through CricCenter, relevant data (such as your player profile and match statistics) may be shared with the league or organization administrators.</p>
        </div>
        <div className="policy-subsection">
          <h4>15.3 Legal Requirements</h4>
          <p>We may disclose your information if required to do so by law, court order, or governmental authority, or if we believe disclosure is necessary to protect our rights, protect your safety or the safety of others, or investigate fraud.</p>
        </div>
        <div className="policy-subsection">
          <h4>15.4 Business Transfers</h4>
          <p>In the event of a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you of any such change and any choices you may have.</p>
        </div>
      </>
    ),
  },
  {
    number: '16',
    title: 'API Access and Third-Party Integrations',
    content: (
      <p>CricCenter may offer API access to authorized third parties. Any third-party integration is governed by our API Terms of Service and applicable data processing agreements. Third parties accessing our API are prohibited from using data for unauthorized purposes or storing data beyond what is necessary for their approved use case.</p>
    ),
  },
  {
    number: '17',
    title: 'Data We Do Not Sell',
    content: (
      <p>CricCenter does not sell, rent, or trade your personal information to third parties for their marketing purposes. We may share aggregated, anonymized data that does not identify any individual for analytical or research purposes.</p>
    ),
  },
  {
    number: '18',
    title: 'User-Generated Content',
    content: (
      <p>Content you post publicly on our Platform, such as match commentary, team announcements, or forum posts, may be visible to other users. Please be mindful of the information you share publicly. We are not responsible for how other users may use publicly posted information.</p>
    ),
  },
  {
    number: '19',
    title: 'Data Retention',
    content: (
      <p>We retain your personal information for as long as your account is active or as needed to provide you with our services. We also retain and use your information to comply with legal obligations, resolve disputes, and enforce our agreements. When data is no longer needed, we securely delete or anonymize it. You can request deletion of your account and associated data by contacting us at privacy@criccenter.com.</p>
    ),
  },
  {
    number: '20',
    title: 'Data Minimization',
    content: (
      <p>We are committed to collecting only the personal data that is necessary for the purposes described in this Privacy Policy. We regularly review the data we collect to ensure it remains relevant and limited to what is needed.</p>
    ),
  },
  {
    number: '21',
    title: 'Security',
    content: (
      <p>We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration, and destruction. These measures include encryption of data in transit and at rest, secure access controls, regular security assessments, and monitoring of our systems. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>
    ),
  },
  {
    number: '22',
    title: 'Account Security',
    content: (
      <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Please notify us immediately at support@criccenter.com if you suspect unauthorized access to your account. Do not share your password with others.</p>
    ),
  },
  {
    number: '23',
    title: 'International Data Transfers',
    content: (
      <p>CricCenter is based in the United States. If you are accessing our Platform from outside the United States, your personal information may be transferred to and processed in the United States or other countries where our service providers operate. These countries may have different data protection laws than your country. By using our Platform, you consent to such transfers. We take appropriate steps to ensure that transfers comply with applicable legal requirements and that your data remains protected.</p>
    ),
  },
  {
    number: '24',
    title: 'Your Rights and Choices',
    content: (
      <>
        <p>Depending on your location, you may have the following rights regarding your personal data:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
          <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
          <li><strong>Deletion:</strong> Request deletion of your personal data, subject to certain exceptions.</li>
          <li><strong>Restriction:</strong> Request that we restrict processing of your data in certain circumstances.</li>
          <li><strong>Portability:</strong> Request a copy of your data in a structured, machine-readable format.</li>
          <li><strong>Objection:</strong> Object to processing of your data for direct marketing or other purposes.</li>
          <li><strong>Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent.</li>
        </ul>
        <p>To exercise any of these rights, please contact us at privacy@criccenter.com. We will respond to your request within the timeframe required by applicable law.</p>
      </>
    ),
  },
  {
    number: '25',
    title: 'India — Additional Notices',
    content: (
      <p>If you are located in India, you have rights under the Information Technology Act, 2000 (as amended in 2008) and associated rules. We comply with the applicable Indian data protection regulations. Our Grievance Officer can be contacted at privacy@criccenter.com for any data-related concerns. We will address grievances within 30 days of receipt.</p>
    ),
  },
  {
    number: '26',
    title: 'Europe and United Kingdom — Additional Notices',
    content: (
      <p>If you are located in the European Economic Area (EEA) or the United Kingdom, additional rights apply to you under the General Data Protection Regulation (GDPR) or the UK GDPR. You have the right to lodge a complaint with your local data protection authority. Our legal bases for processing are described in Section 8 of this policy.</p>
    ),
  },
  {
    number: '27',
    title: 'California — Additional Notices (CCPA/CPRA)',
    content: (
      <>
        <p>If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA), including:</p>
        <ul>
          <li>The right to know what personal information we collect, use, and disclose.</li>
          <li>The right to delete your personal information.</li>
          <li>The right to correct inaccurate personal information.</li>
          <li>The right to opt out of the sale or sharing of personal information.</li>
          <li>The right to non-discrimination for exercising your privacy rights.</li>
        </ul>
        <p>CricCenter does not sell your personal information. To submit a California privacy rights request, contact us at privacy@criccenter.com.</p>
      </>
    ),
  },
  {
    number: '28',
    title: "Children's Privacy — U.S. (COPPA)",
    content: (
      <p>We comply with the Children's Online Privacy Protection Act (COPPA). We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child under 13 has provided us with personal information, please contact us at privacy@criccenter.com and we will promptly delete the information.</p>
    ),
  },
  {
    number: '29',
    title: 'Canada — Additional Notices (PIPEDA)',
    content: (
      <p>If you are a Canadian resident, we comply with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy laws. You have the right to access and correct your personal information. To make a request, contact us at privacy@criccenter.com.</p>
    ),
  },
  {
    number: '30',
    title: 'United Arab Emirates — Additional Notices',
    content: (
      <p>If you are located in the United Arab Emirates, we comply with applicable UAE data protection regulations, including Federal Decree-Law No. 45 of 2021 on Personal Data Protection. You have rights to access, correct, and request deletion of your personal data.</p>
    ),
  },
  {
    number: '31',
    title: 'Saudi Arabia — Additional Notices',
    content: (
      <p>If you are located in Saudi Arabia, we comply with the Personal Data Protection Law (PDPL) of Saudi Arabia. You have rights regarding access to, correction of, and deletion of your personal data. Please contact us at privacy@criccenter.com to exercise these rights.</p>
    ),
  },
  {
    number: '32',
    title: 'Africa — Additional Notices',
    content: (
      <p>If you are located in an African country with applicable data protection laws, we are committed to complying with those laws to the extent applicable. We respect your rights regarding your personal data and will work to fulfill requests made in accordance with local requirements.</p>
    ),
  },
  {
    number: '33',
    title: 'Marketing Communications',
    content: (
      <p>We may send you promotional emails or push notifications about new features, special offers, or other news related to CricCenter. You can opt out of marketing communications at any time by clicking "unsubscribe" in any marketing email or adjusting your notification settings in the app. Please note that even after opting out of marketing communications, we may still send you transactional or service-related messages.</p>
    ),
  },
  {
    number: '34',
    title: 'Third-Party Links',
    content: (
      <p>Our Platform may contain links to third-party websites, applications, or services. This Privacy Policy does not apply to those third-party services. We encourage you to read the privacy policies of any third-party services you visit. We are not responsible for the privacy practices of third parties.</p>
    ),
  },
  {
    number: '35',
    title: 'Analytics Services',
    content: (
      <p>We use third-party analytics services (such as Google Analytics, Firebase, and others) to help us understand how users interact with our Platform. These services may use cookies and similar technologies to collect information about your use of the Platform. The information generated is transmitted to and stored by these services in accordance with their privacy policies.</p>
    ),
  },
  {
    number: '36',
    title: 'Automated Decision-Making',
    content: (
      <p>CricCenter may use automated systems to provide certain features, such as performance recommendations or personalized content. Where decisions are made solely by automated means that significantly affect you, we will inform you and, where required by applicable law, provide you with the right to request human review of such decisions.</p>
    ),
  },
  {
    number: '37',
    title: 'Data Breach Notification',
    content: (
      <p>In the event of a data breach that is likely to result in a risk to your rights and freedoms, we will notify you and the relevant authorities as required by applicable law. We maintain incident response procedures to promptly address any security incidents.</p>
    ),
  },
  {
    number: '38',
    title: 'Aggregated and Anonymized Data',
    content: (
      <p>We may use aggregated or anonymized data that does not identify any individual for research, analytics, and business intelligence purposes. Such data is not subject to this Privacy Policy.</p>
    ),
  },
  {
    number: '39',
    title: 'Changes to This Privacy Policy',
    content: (
      <p>We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of material changes by posting the updated policy on our Platform and, where appropriate, by sending you a notification. Your continued use of the Platform after the effective date of the updated policy constitutes your acceptance of the changes. We encourage you to review this policy periodically.</p>
    ),
  },
  {
    number: '40',
    title: 'Contact Us',
    content: (
      <>
        <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
        <div className="policy-contact-block">
          <p><strong>CricCenter Inc.</strong></p>
          <p><strong>Privacy / Grievance Officer:</strong> Urvesh Patel</p>
          <p><strong>Privacy Email:</strong> <a href="mailto:criccenter.ai@gmail.com">criccenter.ai@gmail.com</a></p>
          <p><strong>Support Email:</strong> <a href="mailto:support@criccenter.com">support@criccenter.com</a></p>
        </div>
        <p>We take all privacy concerns seriously and will respond to your inquiry in a timely manner.</p>
      </>
    ),
  },
  {
    number: '41',
    title: 'Effective Date',
    content: (
      <p>This Privacy Policy is effective as of the date it is posted on our Platform. It was last updated on June 27, 2026.</p>
    ),
  },
];

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="policy-page">
      <div className="policy-page-spacer" />

      <section className="policy-hero-section">
        <span className="policy-badge">LEGAL</span>
        <h1 className="policy-hero-title">
          Privacy <em>Policy</em>
        </h1>
        <p className="policy-hero-subtitle">
          Your privacy matters to us. Learn how CricCenter collects, uses, and protects your personal information.
        </p>
        <p className="policy-effective-date">Effective Date: June 27, 2026 &nbsp;|&nbsp; Last Updated: June 27, 2026</p>
      </section>

      <div className="policy-content-wrapper">
        <div className="policy-sections">
          {sections.map((section) => (
            <section key={section.number} className="policy-section" id={`section-${section.number}`}>
              <div className="policy-section-header">
                <span className="policy-section-number">{section.number}</span>
                <h2 className="policy-section-title">{section.title}</h2>
              </div>
              <div className="policy-section-body">
                {section.content}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
